import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Add as AddIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { PermissionButton } from '@/components/common/PermissionButton/PermissionButton';
import { useToast } from '@/components/common/Toast';
import { useNavigate } from 'react-router-dom';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import {
  useGetSchedulesQuery,
  useDeleteScheduleMutation,
  useCanDeleteScheduleQuery,
} from '../../store/api/scheduleApi';
import { TanStackTableViewWithSchema } from '@/components/common/Table';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import { hasActiveFilters } from '@/utils/helpers';
import Button from '@/components/common/Button';

const ScheduleListPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    page,
    rowsPerPage,
    searchTerm,
    sortKey,
    sortDirection,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    handleFilterChange,
    clearFilters,
    toDetail,
    toEdit,
    toCreate,
  } = useCompletePaginationNavigation(navigate, '/schedule-management/schedules');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  const { showToast } = useToast();

  const queryParams = useMemo(() => {
    const apiParams: Record<string, unknown> = {
      page: page + 1,
      limit: rowsPerPage,
      cf: 'true',
    };
    if (searchTerm) apiParams.search = searchTerm;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        apiParams[key] = value;
      }
    });
    if (sortKey) {
      apiParams.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
    }
    return apiParams;
  }, [page, rowsPerPage, searchTerm, filters, sortKey, sortDirection]);

  const { data, isLoading, error, isFetching } = useGetSchedulesQuery(queryParams as any);
  const [deleteSchedule, { isLoading: isDeleting }] = useDeleteScheduleMutation();
  const { data: canDeleteData } = useCanDeleteScheduleQuery(scheduleToDelete || '', {
    skip: !scheduleToDelete,
  });

  const schedules = data?.schedules ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleDeleteClick = (id: string | number) => {
    setScheduleToDelete(String(id));
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (scheduleToDelete && canDeleteData?.canDelete) {
      try {
        await deleteSchedule(scheduleToDelete).unwrap();
        setDeleteDialogOpen(false);
        setScheduleToDelete(null);
        showToast('Schedule deleted successfully', 'success');
      } catch (err) {
        console.error('Failed to delete schedule:', err);
        showToast('Failed to delete schedule', 'error');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  return (
    <div className="appWrapper">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <div>
          <h3 className="pageTitle" style={{ marginBottom: 0 }}>
            Schedule Management
          </h3>
          <Typography variant="subtitle1" color="textSecondary">
            Manage your asset schedules and activities
          </Typography>
        </div>
      </div>

      <div className="appGridContainer">
        <div className="gridHeader">
          <div className="gridHeadLeft">
            <div className="inlineFormContainer">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                sx={{ height: 40 }}
                startIcon={<FilterListIcon />}
                onClick={clearFilters}
                disabled={!hasActiveFilters(filters)}
              >
                Clear Filters
              </Button>
            </div>
            
          </div>
          <PermissionButton
          module="schedule"
          permissionAction="add"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => toCreate()}
        >
          Add Schedule
        </PermissionButton>
        </div>

        <div className="gridWrapper">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading schedules. Please try again.
            </Alert>
          )}
          <TanStackTableViewWithSchema
            data={schedules}
            rowKey={(row: any) => row.id}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            handleView={(id) => toDetail(String(id))}
            handleEdit={(id) => toEdit(String(id))}
            handleDelete={handleDeleteClick}
            loading={isLoading || isFetching}
            permissionModule="schedule"
            entity="schedule"
            enableAPIConfig={true}
            onFilterChange={handleFilterChange}
            filterValues={filters}
            fallbackHeaders={[]}
          />
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {canDeleteData?.canDelete
              ? 'Are you sure you want to delete this schedule? This action cannot be undone.'
              : canDeleteData?.reason || 'This schedule cannot be deleted.'}
          </DialogContentText>
          {canDeleteData && !canDeleteData.canDelete && canDeleteData.activityCount && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Activity Count:</Typography>
              <Typography variant="body2">Total: {canDeleteData.activityCount.total}</Typography>
              <Typography variant="body2">Active: {canDeleteData.activityCount.active}</Typography>
              <Typography variant="body2">Pending: {canDeleteData.activityCount.pending}</Typography>
              <Typography variant="body2">Completed: {canDeleteData.activityCount.completed}</Typography>
              <Typography variant="body2">Cancelled: {canDeleteData.activityCount.cancelled}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={!canDeleteData?.canDelete || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScheduleListPage;
